// src/utils/imageUpload.js

/**
 * Image upload utility functions
 * For now, we'll use base64 encoding for simplicity
 * In production, you'd want to use Firebase Storage or another cloud service
 */

// Maximum file size (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Allowed image types
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Validate image file
 * @param {File} file - The image file to validate
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'No file selected' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { 
      isValid: false, 
      error: 'Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.' 
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { 
      isValid: false, 
      error: 'File size too large. Please select an image smaller than 5MB.' 
    };
  }

  return { isValid: true, error: null };
};

/**
 * Convert image file to base64
 * @param {File} file - The image file to convert
 * @returns {Promise<string>} - Base64 encoded image string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      resolve(reader.result);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Resize image if it's too large
 * @param {File} file - The image file to resize
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels
 * @param {number} quality - Image quality (0-1)
 * @returns {Promise<string>} - Base64 encoded resized image
 */
export const resizeImage = (file, maxWidth = 1200, maxHeight = 800, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to base64
      const base64 = canvas.toDataURL(file.type, quality);
      resolve(base64);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    // Load image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Handle image upload with validation and processing
 * @param {File} file - The image file to upload
 * @param {Object} options - Upload options
 * @returns {Promise<Object>} - Upload result with URL and metadata
 */
export const handleImageUpload = async (file, options = {}) => {
  const {
    resize = true,
    maxWidth = 1200,
    maxHeight = 800,
    quality = 0.8
  } = options;

  try {
    // Validate file
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Process image
    let imageUrl;
    if (resize && file.size > 500 * 1024) { // Resize if larger than 500KB
      imageUrl = await resizeImage(file, maxWidth, maxHeight, quality);
    } else {
      imageUrl = await fileToBase64(file);
    }

    return {
      success: true,
      url: imageUrl,
      metadata: {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Create image upload handler for React Quill
 * @param {Function} onUploadStart - Callback when upload starts
 * @param {Function} onUploadEnd - Callback when upload ends
 * @returns {Function} - Image handler function
 */
export const createQuillImageHandler = (onUploadStart, onUploadEnd) => {
  return function() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        if (onUploadStart) onUploadStart();

        const result = await handleImageUpload(file);
        
        if (result.success) {
          // Insert image into editor
          const range = this.quill.getSelection();
          this.quill.insertEmbed(range.index, 'image', result.url);
          
          // Move cursor after image
          this.quill.setSelection(range.index + 1);
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('Image upload error:', error);
        alert(`Image upload failed: ${error.message}`);
      } finally {
        if (onUploadEnd) onUploadEnd();
      }
    };
  };
};

/**
 * Extract images from HTML content
 * @param {string} htmlContent - HTML content to extract images from
 * @returns {Array} - Array of image URLs found in content
 */
export const extractImagesFromContent = (htmlContent) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const images = doc.querySelectorAll('img');
  
  return Array.from(images).map(img => ({
    src: img.src,
    alt: img.alt || '',
    width: img.width || null,
    height: img.height || null
  }));
};

/**
 * Get image dimensions from URL
 * @param {string} imageUrl - Image URL
 * @returns {Promise<Object>} - Image dimensions
 */
export const getImageDimensions = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};